/**
 * Filtered view over a study file. Precomputed summaries serve the unfiltered case; when a
 * filter is active, counts are recomputed over the (small) record arrays. Lookups are by id.
 */
import { subjectPasses } from '../ward/layout.ts';
import type {
  AdverseEvent,
  ConMed,
  DashboardFilters,
  Deviation,
  Dosing,
  Ecg,
  FormRecord,
  Lab,
  Query,
  RecordRef,
  Site,
  StudyFile,
  Subject,
  Visit,
  Vitals,
} from '@clinspace/shared';

export interface Dated {
  date?: string;
  onsetDate?: string;
  startDate?: string;
  raisedAt?: string;
}

export class StudyView {
  private readonly subjectById = new Map<string, Subject>();
  private readonly siteById = new Map<string, Site>();
  private readonly visitById = new Map<string, Visit>();
  private readonly visitOrder = new Map<string, number>();
  private readonly formById = new Map<string, FormRecord>();
  private readonly labById = new Map<string, Lab>();
  private readonly aeById = new Map<string, AdverseEvent>();
  private readonly conMedById = new Map<string, ConMed>();
  private readonly deviationById = new Map<string, Deviation>();
  private readonly dosingById = new Map<string, Dosing>();
  private readonly vitalsById = new Map<string, Vitals>();
  private readonly ecgById = new Map<string, Ecg>();
  /** Live queries for this study (seed plus raised), kept in sync by the room scene. */
  queries: Query[] = [];

  constructor(readonly file: StudyFile) {
    for (const s of file.subjects) this.subjectById.set(s.id, s);
    for (const s of file.sites) this.siteById.set(s.id, s);
    for (const v of file.visits) this.visitById.set(v.id, v);
    file.study.visitSchedule.forEach((v, i) => this.visitOrder.set(v.code, i));
    for (const f of file.forms) this.formById.set(f.id, f);
    for (const l of file.labs) this.labById.set(l.id, l);
    for (const a of file.adverseEvents) this.aeById.set(a.id, a);
    for (const c of file.conMeds) this.conMedById.set(c.id, c);
    for (const d of file.deviations) this.deviationById.set(d.id, d);
    for (const d of file.dosing) this.dosingById.set(d.id, d);
    for (const v of file.vitals) this.vitalsById.set(v.id, v);
    for (const e of file.ecgs) this.ecgById.set(e.id, e);
    this.queries = [...file.seedQueries];
  }

  get code(): string {
    return this.file.study.code;
  }

  subject(id: string): Subject | undefined {
    return this.subjectById.get(id);
  }
  site(id: string): Site | undefined {
    return this.siteById.get(id);
  }
  siteLabel(id: string): string {
    const s = this.siteById.get(id);
    return s ? `${s.id} ${s.city}` : id;
  }
  visit(id: string): Visit | undefined {
    return this.visitById.get(id);
  }
  form(id: string): FormRecord | undefined {
    return this.formById.get(id);
  }
  lab(id: string): Lab | undefined {
    return this.labById.get(id);
  }
  ae(id: string): AdverseEvent | undefined {
    return this.aeById.get(id);
  }
  conMed(id: string): ConMed | undefined {
    return this.conMedById.get(id);
  }
  deviation(id: string): Deviation | undefined {
    return this.deviationById.get(id);
  }
  dosing(id: string): Dosing | undefined {
    return this.dosingById.get(id);
  }
  vitals(id: string): Vitals | undefined {
    return this.vitalsById.get(id);
  }
  ecg(id: string): Ecg | undefined {
    return this.ecgById.get(id);
  }

  visitIndex(code: string): number {
    return this.visitOrder.get(code) ?? -1;
  }

  /* ---------- Filters ---------- */

  static isFiltered(f: DashboardFilters): boolean {
    return !!(
      f.siteId ||
      f.arm ||
      f.visitFrom ||
      f.visitTo ||
      f.dateFrom ||
      f.dateTo ||
      f.subjectId ||
      f.subjectIds?.length ||
      f.sex ||
      f.status ||
      f.flag ||
      f.ageBand ||
      f.bmiBucket
    );
  }

  /** Subjects passing the site and arm filters. */
  subjects(f: DashboardFilters = {}): Subject[] {
    return this.file.subjects.filter((s) => subjectPasses(s, f));
  }

  subjectIds(f: DashboardFilters = {}): Set<string> {
    return new Set(this.subjects(f).map((s) => s.id));
  }

  private visitInRange(code: string | undefined, f: DashboardFilters): boolean {
    if (!code || (!f.visitFrom && !f.visitTo)) return true;
    const i = this.visitIndex(code);
    if (i < 0) return true;
    if (f.visitFrom && i < this.visitIndex(f.visitFrom)) return false;
    if (f.visitTo && i > this.visitIndex(f.visitTo)) return false;
    return true;
  }

  private dateInRange(date: string | undefined, f: DashboardFilters): boolean {
    if (!date || (!f.dateFrom && !f.dateTo)) return true;
    if (f.dateFrom && date < f.dateFrom) return false;
    if (f.dateTo && date > f.dateTo) return false;
    return true;
  }

  /** Generic record filter: subject (site/arm), visit range, date range. */
  filter<T extends { subjectId: string; visitCode?: string } & Dated>(
    records: T[],
    f: DashboardFilters = {},
  ): T[] {
    if (!StudyView.isFiltered(f)) return records;
    const ids = this.subjectIds(f);
    return records.filter(
      (r) =>
        ids.has(r.subjectId) &&
        this.visitInRange(r.visitCode, f) &&
        this.dateInRange(r.date ?? r.onsetDate ?? r.startDate ?? r.raisedAt?.slice(0, 10), f),
    );
  }

  visits(f: DashboardFilters = {}): Visit[] {
    return this.filter(this.file.visits, f);
  }
  forms(f: DashboardFilters = {}): FormRecord[] {
    return this.filter(this.file.forms, f);
  }
  labs(f: DashboardFilters = {}): Lab[] {
    return this.filter(this.file.labs, f);
  }
  adverseEvents(f: DashboardFilters = {}): AdverseEvent[] {
    return this.filter(this.file.adverseEvents, f);
  }
  deviations(f: DashboardFilters = {}): Deviation[] {
    return this.filter(this.file.deviations, f);
  }
  dosings(f: DashboardFilters = {}): Dosing[] {
    return this.filter(this.file.dosing, f);
  }
  vitalsList(f: DashboardFilters = {}): Vitals[] {
    return this.filter(this.file.vitals, f);
  }
  queriesFiltered(f: DashboardFilters = {}): Query[] {
    if (!StudyView.isFiltered(f)) return this.queries;
    const ids = this.subjectIds(f);
    return this.queries.filter(
      (q) => ids.has(q.subjectId) && this.dateInRange(q.raisedAt.slice(0, 10), f),
    );
  }

  openQueryCount(subjectId?: string): number {
    return this.queries.filter(
      (q) => q.status === 'Open' && (!subjectId || q.subjectId === subjectId),
    ).length;
  }

  /* ---------- Record references ---------- */

  ref(
    subjectId: string,
    form: string,
    recordId: string,
    visit?: string,
    field?: string,
  ): RecordRef {
    const r: RecordRef = { study: this.code, subjectId, form, recordId };
    if (visit) r.visit = visit;
    if (field) r.field = field;
    return r;
  }

  visitName(visitId: string | undefined): string | undefined {
    return visitId ? this.visitById.get(visitId)?.visitName : undefined;
  }

  /** Arms present in the study, for the filter bar. */
  arms(): string[] {
    return this.file.study.arms.map((a) => a.name);
  }

  upsertQuery(q: Query): void {
    const i = this.queries.findIndex((x) => x.id === q.id);
    if (i >= 0) this.queries[i] = q;
    else this.queries.push(q);
  }
}
